// frontend/src/components/PlayerQuadrant.jsx
// Offensive vs Defensive percentile quadrant chart (pure SVG)
// Toggle between per-36 minutes and per-game normalisation.
import { useMemo, useState } from 'react';
import { Crosshair } from 'lucide-react';

// ─── League distributions (291 players, ≥10 games, 2024-25) ─────────────────
// Per-game: raw season averages
// Per-36:   each player's per-game avg scaled by (36 / avg_min_per_game)
// FG% and PM splits are rate/game-level metrics — identical in both modes.
const LEAGUE = {
  per_game: {
    pts:    { mean: 9.481,  stdDev: 6.513 },
    ast:    { mean: 2.194,  stdDev: 1.820 },
    fg_pct: { mean: 0.456,  stdDev: 0.072 },
    stl:    { mean: 0.703,  stdDev: 0.404 },
    blk:    { mean: 0.424,  stdDev: 0.400 },
    off_pm: { mean: 2.396,  stdDev: 5.680 },
    def_pm: { mean: 1.246,  stdDev: 5.824 },
  },
  per_36: {
    pts:    { mean: 15.936, stdDev: 5.511 },
    ast:    { mean: 3.643,  stdDev: 2.163 },
    fg_pct: { mean: 0.467,  stdDev: 0.091 },
    stl:    { mean: 1.228,  stdDev: 0.561 },
    blk:    { mean: 0.815,  stdDev: 0.739 },
    off_pm: { mean: 2.396,  stdDev: 5.680 },
    def_pm: { mean: 1.246,  stdDev: 5.824 },
  },
};

const OFF_WEIGHTS = { pts: 0.375, ast: 0.225, fg_pct: 0.15, off_pm: 0.25 };
const DEF_WEIGHTS = { stl: 0.4125, blk: 0.3375, def_pm: 0.25 };

// ─── Math helpers ─────────────────────────────────────────────────────────────

function normCDF(z) {
  if (z < -8) return 0;
  if (z > 8)  return 100;
  const a1=0.254829592, a2=-0.284496736, a3=1.421413741,
        a4=-1.453152027, a5=1.061405429, p=0.3275911;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.sqrt(2);
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - ((((a5*t+a4)*t+a3)*t+a2)*t+a1)*t*Math.exp(-x*x);
  return Math.round(50 * (1.0 + sign * y));
}

function zScore(value, key, leagueDists) {
  const dist = leagueDists[key];
  if (!dist || dist.stdDev === 0) return 0;
  return (value - dist.mean) / dist.stdDev;
}

function percentile(value, key, leagueDists) {
  return normCDF(zScore(value, key, leagueDists));
}

function compositePercentile(values, weights, leagueDists) {
  let total = 0, wSum = 0;
  for (const [key, w] of Object.entries(weights)) {
    const v = values[key];
    if (v === undefined || v === null || isNaN(v)) continue;
    total += percentile(v, key, leagueDists) * w;
    wSum  += w;
  }
  return wSum > 0 ? total / wSum : 50;
}

// ─── Quadrant config ──────────────────────────────────────────────────────────

const QUADRANTS = [
  { label: 'Underperformer',  xRange: [0,50],  yRange: [0,50],   textColor: '#9ca3af', fill: '#37415122' },
  { label: 'Offensive Threat',xRange: [0,50],  yRange: [50,100], textColor: '#fb923c', fill: '#f9731610' },
  { label: 'Stopper',         xRange: [50,100],yRange: [0,50],   textColor: '#60a5fa', fill: '#3b82f610' },
  { label: '2-Way Asset',     xRange: [50,100],yRange: [50,100], textColor: '#4ade80', fill: '#22c55e10' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function PlayerQuadrant({ player, gameLogs }) {
  const [mode, setMode] = useState('per_36'); // 'per_game' | 'per_36'

  const logs = (gameLogs || []).filter((g) => parseInt(g.min) > 0);

  // ── Compute both per-game and per-36 averages ──────────────────────────────
  const playerAvg = useMemo(() => {
    if (logs.length === 0) return null;
    const n = logs.length;

    // Parse minutes — API returns "MM" or "MM:SS"
    const parseMin = (m) => {
      const s = String(m || '0');
      const parts = s.split(':');
      const mins = parseInt(parts[0]) || 0;
      const secs = parts[1] ? parseInt(parts[1]) || 0 : 0;
      return mins + secs / 60;
    };

    const totalMin = logs.reduce((s, g) => s + parseMin(g.min), 0);
    const avgMin   = totalMin / n;
    if (avgMin < 1) return null;

    const sumStat = (key) => logs.reduce((s, g) => s + (Number(g[key]) || 0), 0);

    const avgPtsRaw   = sumStat('pts') / n;
    const avgStlBlk   = (sumStat('stl') + sumStat('blk')) / n;

    // PM splits — game-level, same in both modes
    const offGames = logs.filter((g) => (Number(g.pts) || 0) >= avgPtsRaw);
    const off_pm   = offGames.length > 0
      ? offGames.reduce((s, g) => s + (Number(g.plus_minus) || 0), 0) / offGames.length
      : 0;
    const defGames = logs.filter((g) => ((Number(g.stl)||0) + (Number(g.blk)||0)) >= avgStlBlk);
    const def_pm   = defGames.length > 0
      ? defGames.reduce((s, g) => s + (Number(g.plus_minus) || 0), 0) / defGames.length
      : 0;

    const fgm = sumStat('fgm'), fga = sumStat('fga');
    const fg_pct = fga > 0 ? fgm / fga : 0;
    const scale36 = 36 / avgMin;

    return {
      per_game: {
        pts:    avgPtsRaw,
        ast:    sumStat('ast') / n,
        stl:    sumStat('stl') / n,
        blk:    sumStat('blk') / n,
        fg_pct, off_pm, def_pm,
      },
      per_36: {
        pts:    avgPtsRaw * scale36,
        ast:    (sumStat('ast') / n) * scale36,
        stl:    (sumStat('stl') / n) * scale36,
        blk:    (sumStat('blk') / n) * scale36,
        fg_pct, off_pm, def_pm,
      },
      avgMin,
      scale36,
      plus_minus: sumStat('plus_minus') / n,
      offGames: offGames.length,
      defGames: defGames.length,
    };
  }, [logs]);

  const leagueDists = LEAGUE[mode];
  const stats = playerAvg ? playerAvg[mode] : null;

  const offPct = useMemo(
    () => stats ? compositePercentile(stats, OFF_WEIGHTS, leagueDists) : null,
    [stats, leagueDists]
  );
  const defPct = useMemo(
    () => stats ? compositePercentile(stats, DEF_WEIGHTS, leagueDists) : null,
    [stats, leagueDists]
  );

  if (!player || !playerAvg || offPct === null || defPct === null) return null;

  const playerName = `${player.first_name} ${player.last_name}`;
  const isP36 = mode === 'per_36';

  const activeQ = QUADRANTS.find(
    (q) => offPct >= q.yRange[0] && offPct < q.yRange[1] &&
           defPct >= q.xRange[0] && defPct < q.xRange[1]
  ) || QUADRANTS[0];

  // ── SVG layout ──────────────────────────────────────────────────────────────
  const W = 500, H = 420;
  const PAD = { top: 20, right: 24, bottom: 52, left: 52 };
  const PW = W - PAD.left - PAD.right;
  const PH = H - PAD.top  - PAD.bottom;

  const toX = (d) => PAD.left + (d / 100) * PW;
  const toY = (o) => PAD.top  + PH - (o / 100) * PH;

  const midX = toX(50), midY = toY(50);
  const dotX = toX(Math.min(99, Math.max(1, defPct)));
  const dotY = toY(Math.min(99, Math.max(1, offPct)));

  const labelX = Math.min(Math.max(dotX, PAD.left + 36), W - PAD.right - 36);
  const labelY = dotY < PAD.top + 28 ? dotY + 28 : dotY - 16;

  const fmtPM  = (v) => (v >= 0 ? '+' : '') + v.toFixed(1);
  const fmtStat = (v) => v.toFixed(1);
  const suffix  = isP36 ? '/36' : '/g';

  return (
    <div className="bg-gray-800/50 rounded-2xl p-6 border border-gray-700/50">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <Crosshair className="h-6 w-6 text-orange-500" />
          <h4 className="text-xl font-bold text-white">Player Profile Quadrant</h4>
        </div>

        {/* Toggle */}
        <div className="flex items-center bg-gray-900/60 rounded-lg p-0.5 border border-gray-700/50">
          {[['per_game', 'Per Game'], ['per_36', 'Per 36']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setMode(val)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                mode === val
                  ? 'bg-orange-500 text-white shadow'
                  : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-gray-400 text-sm mb-4">
        {isP36
          ? `Stats normalised to per-36 minutes (${playerAvg.avgMin.toFixed(1)} min/g → ×${playerAvg.scale36.toFixed(2)}). Offense = PTS + AST + FG% + Off PM. Defense = STL + BLK + Def PM.`
          : 'Raw per-game season-to-date averages vs all NBA players. Offense = PTS + AST + FG% + Off PM. Defense = STL + BLK + Def PM.'
        }
      </p>

      {/* Badge */}
      <div
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold mb-5"
        style={{ backgroundColor: `${activeQ.textColor}18`, color: activeQ.textColor, border: `1px solid ${activeQ.textColor}44` }}
      >
        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: activeQ.textColor }} />
        {playerName} — {activeQ.label}
      </div>

      {/* SVG */}
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" xmlns="http://www.w3.org/2000/svg">

        {/* Quadrant fills */}
        {QUADRANTS.map((q) => (
          <rect
            key={q.label}
            x={toX(q.xRange[0])} y={toY(q.yRange[1])}
            width={toX(q.xRange[1]) - toX(q.xRange[0])}
            height={toY(q.yRange[0]) - toY(q.yRange[1])}
            fill={q.fill}
          />
        ))}

        {/* Grid lines every 25% */}
        {[25, 75].map((v) => (
          <g key={v}>
            <line x1={toX(v)} y1={PAD.top} x2={toX(v)} y2={toY(0)} stroke="#374151" strokeWidth="1" strokeDasharray="4 4" />
            <line x1={PAD.left} y1={toY(v)} x2={toX(100)} y2={toY(v)} stroke="#374151" strokeWidth="1" strokeDasharray="4 4" />
          </g>
        ))}

        {/* Centre dividers */}
        <line x1={midX} y1={PAD.top} x2={midX} y2={toY(0)} stroke="#6b7280" strokeWidth="1.5" />
        <line x1={PAD.left} y1={midY} x2={toX(100)} y2={midY} stroke="#6b7280" strokeWidth="1.5" />

        {/* Quadrant labels */}
        <text x={PAD.left+8}  y={toY(0)-8}   fontSize="11" fill="#6b728099" fontWeight="600">Underperformer</text>
        <text x={PAD.left+8}  y={PAD.top+16} fontSize="11" fill="#fb923c88" fontWeight="600">Offensive Threat</text>
        <text x={toX(52)}     y={toY(0)-8}   fontSize="11" fill="#60a5fa88" fontWeight="600">Stopper</text>
        <text x={toX(52)}     y={PAD.top+16} fontSize="11" fill="#4ade8088" fontWeight="600">2-Way Asset</text>

        {/* Axes */}
        <line x1={PAD.left} y1={toY(0)} x2={toX(100)} y2={toY(0)} stroke="#4b5563" strokeWidth="1.5" />
        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={toY(0)} stroke="#4b5563" strokeWidth="1.5" />

        {/* X-axis ticks */}
        {[0,25,50,75,100].map((v) => (
          <g key={v}>
            <line x1={toX(v)} y1={toY(0)} x2={toX(v)} y2={toY(0)+5} stroke="#6b7280" strokeWidth="1" />
            <text x={toX(v)} y={toY(0)+18} textAnchor="middle" fontSize="11" fill="#9ca3af">{v}th</text>
          </g>
        ))}
        <text x={PAD.left+PW/2} y={H-4} textAnchor="middle" fontSize="12" fill="#d1d5db" fontWeight="600">
          {`Defensive Percentile  (STL + BLK + Def PM${isP36 ? ', per 36' : ''})`}
        </text>

        {/* Y-axis ticks */}
        {[0,25,50,75,100].map((v) => (
          <g key={v}>
            <line x1={PAD.left-5} y1={toY(v)} x2={PAD.left} y2={toY(v)} stroke="#6b7280" strokeWidth="1" />
            <text x={PAD.left-8} y={toY(v)+4} textAnchor="end" fontSize="11" fill="#9ca3af">{v}th</text>
          </g>
        ))}
        <text
          transform={`rotate(-90, ${PAD.left-38}, ${PAD.top+PH/2})`}
          x={PAD.left-38} y={PAD.top+PH/2}
          textAnchor="middle" fontSize="12" fill="#d1d5db" fontWeight="600"
        >
          {`Offensive Percentile  (PTS + AST + FG% + Off PM${isP36 ? ', per 36' : ''})`}
        </text>

        {/* Player dot + crosshairs */}
        <line x1={dotX} y1={dotY} x2={dotX} y2={toY(0)} stroke={activeQ.textColor} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
        <line x1={PAD.left} y1={dotY} x2={dotX} y2={dotY} stroke={activeQ.textColor} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
        <circle cx={dotX} cy={dotY} r="14" fill={activeQ.textColor} opacity="0.12" />
        <circle cx={dotX} cy={dotY} r="9"  fill={activeQ.textColor} opacity="0.28" />
        <circle cx={dotX} cy={dotY} r="6"  fill={activeQ.textColor} />

        {/* Name label */}
        <rect x={labelX-36} y={labelY-13} width="72" height="18" rx="4" fill="#111827" opacity="0.85" />
        <text x={labelX} y={labelY} textAnchor="middle" fontSize="11" fill={activeQ.textColor} fontWeight="700">
          {player.first_name[0]}. {player.last_name}
        </text>
      </svg>

      {/* Stat breakdown */}
      <div className="grid grid-cols-2 gap-4 mt-4">
        {/* Offense */}
        <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-4">
          <div className="text-orange-400 font-bold text-sm mb-3">
            Offense — {Math.round(offPct)}th percentile
          </div>
          <div className="space-y-1.5 text-xs text-gray-400">
            <div className="flex justify-between">
              <span>PTS{suffix}</span>
              <span className="text-gray-300 font-semibold">
                {fmtStat(stats.pts)}{' '}
                <span className="text-gray-500">({percentile(stats.pts,'pts',leagueDists)}th)</span>
              </span>
            </div>
            <div className="flex justify-between">
              <span>AST{suffix}</span>
              <span className="text-gray-300 font-semibold">
                {fmtStat(stats.ast)}{' '}
                <span className="text-gray-500">({percentile(stats.ast,'ast',leagueDists)}th)</span>
              </span>
            </div>
            <div className="flex justify-between">
              <span>FG%</span>
              <span className="text-gray-300 font-semibold">
                {(stats.fg_pct*100).toFixed(1)}%{' '}
                <span className="text-gray-500">({percentile(stats.fg_pct,'fg_pct',leagueDists)}th)</span>
              </span>
            </div>
            <div className="flex justify-between border-t border-orange-500/20 pt-1.5 mt-1.5">
              <span className="text-orange-300/70">Off PM (high-scoring games)</span>
              <span className="text-orange-300 font-semibold">
                {fmtPM(stats.off_pm)}{' '}
                <span className="text-gray-500">({percentile(stats.off_pm,'off_pm',leagueDists)}th) · {playerAvg.offGames}g</span>
              </span>
            </div>
          </div>
        </div>

        {/* Defense */}
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
          <div className="text-blue-400 font-bold text-sm mb-3">
            Defense — {Math.round(defPct)}th percentile
          </div>
          <div className="space-y-1.5 text-xs text-gray-400">
            <div className="flex justify-between">
              <span>STL{suffix}</span>
              <span className="text-gray-300 font-semibold">
                {fmtStat(stats.stl)}{' '}
                <span className="text-gray-500">({percentile(stats.stl,'stl',leagueDists)}th)</span>
              </span>
            </div>
            <div className="flex justify-between">
              <span>BLK{suffix}</span>
              <span className="text-gray-300 font-semibold">
                {fmtStat(stats.blk)}{' '}
                <span className="text-gray-500">({percentile(stats.blk,'blk',leagueDists)}th)</span>
              </span>
            </div>
            <div className="flex justify-between border-t border-blue-500/20 pt-1.5 mt-1.5">
              <span className="text-blue-300/70">Def PM (high-def games)</span>
              <span className="text-blue-300 font-semibold">
                {fmtPM(stats.def_pm)}{' '}
                <span className="text-gray-500">({percentile(stats.def_pm,'def_pm',leagueDists)}th) · {playerAvg.defGames}g</span>
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Overall PM/g</span>
              <span className="text-gray-400">{fmtPM(playerAvg.plus_minus)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
